if (!customElements.get('wolff-collection-showcase')) {
  customElements.define('wolff-collection-showcase', class extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('[data-showcase-slide]')];
      if (!this.slides.length) return;
      this.events = new AbortController();
      this.index = 0;
      this.pagination = this.querySelector('[data-pagination]');
      this.dots = [...this.querySelectorAll('[data-slide]')];
      this.pause = this.querySelector('[data-pause]');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.visible = false;
      this.hovered = false;
      this.focused = false;
      this.paused = this.motion.matches;
      this.syncPauseLabel();
      const listen = (target, event, handler) => target?.addEventListener(event, handler, {signal:this.events.signal});
      listen(this.querySelector('[data-prev]'), 'click', () => this.show(this.index - 1, true));
      listen(this.querySelector('[data-next]'), 'click', () => this.show(this.index + 1, true));
      this.dots.forEach(dot => listen(dot, 'click', () => this.show(Number(dot.dataset.slide), true)));
      listen(this, 'keydown', event => {
        if (!['ArrowLeft','ArrowRight'].includes(event.key) || event.target.closest('input,textarea,select')) return;
        event.preventDefault();
        this.show(this.index + (event.key === 'ArrowRight' ? 1 : -1), true);
      });
      this.slides.forEach(slide => {
        const media = slide.querySelector('.wolff-showcase__media');
        listen(media, 'pointerdown', event => {
          if (event.pointerType === 'touch' && event.isPrimary) this.touch = {x:event.clientX,y:event.clientY};
        });
        listen(media, 'pointerup', event => {
          if (!this.touch || event.pointerType !== 'touch') return;
          const dx = event.clientX - this.touch.x, dy = event.clientY - this.touch.y;
          this.touch = null;
          if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) this.show(this.index + (dx < 0 ? 1 : -1), true);
        });
        listen(media, 'pointercancel', () => { this.touch = null; });
      });
      listen(this.pause, 'click', () => {
        this.paused = !this.paused;
        this.syncPauseLabel();
        this.updateTimer();
      });
      listen(this, 'pointerenter', event => { if (event.pointerType === 'mouse') { this.hovered = true; this.updateTimer(); } });
      listen(this, 'pointerleave', () => { this.hovered = false; this.updateTimer(); });
      listen(this, 'focusin', () => { this.focused = true; this.updateTimer(); });
      listen(this, 'focusout', () => queueMicrotask(() => { this.focused = this.contains(document.activeElement); this.updateTimer(); }));
      listen(document, 'visibilitychange', () => this.updateTimer());
      listen(this.motion, 'change', () => {
        if (this.motion.matches) this.paused = true;
        this.syncPauseLabel();
        this.updateTimer();
      });
      listen(document, 'shopify:block:select', event => {
        const slide = event.target.closest('[data-showcase-slide]');
        const index = this.slides.indexOf(slide);
        if (index >= 0) this.show(index, true);
      });
      this.observer = new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting; this.updateTimer(); });
      this.observer.observe(this);
      this.classList.add('is-ready');
      this.show(0);
    }
    show(index, announce = false) {
      const next = (index + this.slides.length) % this.slides.length;
      const focus = this.pagination?.contains(document.activeElement) ? document.activeElement : null;
      const old = this.slides[this.index];
      if (old !== this.slides[next] && old.contains(document.activeElement) && !focus) this.querySelector('[data-next]')?.focus({preventScroll:true});
      this.index = next;
      this.slides.forEach((slide, i) => {
        slide.hidden = i !== next;
        slide.classList.toggle('is-active', i === next);
        slide.setAttribute('aria-hidden', String(i !== next));
        slide.inert = i !== next;
      });
      if (this.pagination) this.slides[next].querySelector('.wolff-showcase__copy').prepend(this.pagination);
      this.dots.forEach((dot,i) => {
        if (i === next) dot.setAttribute('aria-current','true');
        else dot.removeAttribute('aria-current');
      });
      if (focus && document.activeElement !== focus) focus.focus({preventScroll:true});
      if (announce) this.querySelector('[data-status]').textContent = `${next + 1} / ${this.slides.length}: ${this.slides[next].querySelector('h2')?.textContent || ''}`;
      this.updateTimer();
    }
    syncPauseLabel() {
      if (!this.pause) return;
      this.pause.setAttribute('aria-pressed', String(this.paused));
      this.pause.textContent = this.pause.dataset[this.paused ? 'playLabel' : 'pauseLabel'];
    }
    syncVideos() {
      this.slides?.forEach((slide, index) => {
        const video = slide.querySelector('video.wolff-showcase__video');
        if (!video) return;
        if (index === this.index && this.visible && !this.paused && !document.hidden) {
          const attempt = video.play();
          attempt?.catch(() => {});
        } else video.pause();
      });
    }
    updateTimer() {
      clearTimeout(this.timer);
      this.syncVideos();
      if (this.dataset.autoplay !== 'true' || this.dataset.editor === 'true' || this.slides.length < 2 || this.motion.matches || !this.visible || this.paused || this.hovered || this.focused || document.hidden) return;
      this.timer = setTimeout(() => this.show(this.index + 1), Math.max(4, Number(this.dataset.speed) || 7) * 1000);
    }
    disconnectedCallback() {
      this.slides?.forEach(slide => slide.querySelectorAll('video').forEach(video => video.pause()));
      clearTimeout(this.timer);
      this.observer?.disconnect();
      this.events?.abort();
    }
  });
}
